import { Injectable, inject, signal } from '@angular/core';
import {
  Auth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  signInWithPopup,
  sendPasswordResetEmail,
  user
} from '@angular/fire/auth';
import { GoogleAuthProvider } from 'firebase/auth';
import {
  Firestore,
  doc,
  setDoc,
  getDoc,
  collection,
  getDocs,
  query,
  where,
  limit
} from '@angular/fire/firestore';
import { Router } from '@angular/router';
import { Observable, from } from 'rxjs';
import { UserProfile } from '../models/user.model';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private auth = inject(Auth);
  private firestore = inject(Firestore);
  private router = inject(Router);

  // Signals for reactive state management
  currentUser = signal<any>(null);
  userProfile = signal<UserProfile | null>(null);
  loading = signal(true);

  constructor() {
    // Listen to auth state changes
    onAuthStateChanged(this.auth, async (user: any) => {
      this.currentUser.set(user);

      if (user) {
        await this.loadUserProfile(user.uid);
      } else {
        this.userProfile.set(null);
      }

      this.loading.set(false);
    });
  }



  /**
   * Check if current user can access data recovery tool
   * ✅ Available to ALL authenticated users
   * Data recovery helps users recover their own visit data from browser storage
   */
  canAccessDataRecovery(): boolean {
    const user = this.currentUser();
    // Allow all authenticated users to access data recovery
    return !!user;
  }

  // Sign in with email and password
  async signIn(email: string, password: string): Promise<void> {
    try {
      const userCredential = await signInWithEmailAndPassword(this.auth, email, password);
      await this.loadUserProfile(userCredential.user.uid);

      // Navigate to home page after login
      await this.router.navigate(['/']);
    } catch (error) {
      console.error('Sign in error:', error);
      throw error;
    }
  }

  // Sign in with Google
  async signInWithGoogle(): Promise<void> {
    try {
      const provider = new GoogleAuthProvider();
      // Add additional scopes if needed
      provider.addScope('profile');
      provider.addScope('email');

      // Optional: Set custom parameters
      provider.setCustomParameters({
        prompt: 'select_account' // Always show account selection
      });

      const userCredential = await signInWithPopup(this.auth, provider);

      // Check if user profile exists, if not create one
      const profileExists = await this.checkProfileExists(userCredential.user.uid);
      if (!profileExists) {
        await this.createUserProfile(userCredential.user);
      }

      await this.loadUserProfile(userCredential.user.uid);

      // Navigate to home page after Google login
      await this.router.navigate(['/']);
    } catch (error: any) {
      console.error('Google sign in error:', error);

      // Handle specific error cases
      if (error.code === 'auth/popup-closed-by-user') {
        throw new Error('Giriş işlemi iptal edildi');
      } else if (error.code === 'auth/popup-blocked') {
        throw new Error('Popup engellenmiş. Lütfen popup engelleyiciyi kapatın.');
      }

      throw error;
    }
  }

  // Register new user
  async register(
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    role: 'ADMIN' | 'PHYSICIAN' = 'PHYSICIAN'
  ): Promise<void> {
    try {
      const userCredential = await createUserWithEmailAndPassword(this.auth, email, password);

      // Create user profile
      const profile: UserProfile = {
        id: userCredential.user.uid,
        email,
        firstName,
        lastName,
        role,
        createdAt: new Date(),
        updatedAt: new Date(),
        isActive: true,
        termsAccepted: false
      };

      await setDoc(doc(this.firestore, 'users', userCredential.user.uid), profile);

      await this.loadUserProfile(userCredential.user.uid);
      await this.router.navigate(['/patients']);
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  }

  // Sign out
  async signOut(): Promise<void> {
    try {
      await signOut(this.auth);
      this.currentUser.set(null);
      this.userProfile.set(null);
      await this.router.navigate(['/']); // Navigate to home instead of login page
    } catch (error) {
      console.error('Sign out error:', error);
      throw error;
    }
  }

  // Reset password
  async resetPassword(email: string): Promise<void> {
    try {
      await sendPasswordResetEmail(this.auth, email);
    } catch (error) {
      console.error('Password reset error:', error);
      throw error;
    }
  }

  // Load user profile from Firestore
  async loadUserProfile(uid: string): Promise<void> {
    try {
      console.log('🔄 Loading user profile for UID:', uid);

      const docRef = doc(this.firestore, 'users', uid);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data() as UserProfile;

        // Ensure id field is set (for backward compatibility with older profiles)
        if (!data.id) {
          data.id = uid;
        }

        console.log('✅ User profile loaded from Firestore:', {
          email: data.email,
          centerId: data.centerId,
          role: data.role,
          id: data.id,
          uid: data.uid
        });

        this.userProfile.set(data);
      } else {
        console.log('⚠️ User profile not found');
        const user = this.currentUser();
        if (user) {
          // CRITICAL FIX: Check if user has a pending invitation
          // If they do, DON'T auto-create profile - let invitation acceptance flow handle it
          const hasPendingInvitation = await this.checkPendingInvitation(user.email);

          if (hasPendingInvitation) {
            console.log('📧 User has pending invitation - skipping auto-profile creation');
            console.log('⏳ Waiting for invitation acceptance to create profile...');
            // Don't create profile - the invitation acceptance flow will handle it
            return;
          } else {
            console.log('🆕 No pending invitation found, creating default profile...');
            // Only auto-create for users without invitations (e.g., Google OAuth)
            await this.createUserProfile(user);
          }
        }
      }
    } catch (error) {
      console.error('❌ Error loading user profile:', error);
      throw error; // Throw to prevent login with invalid profile
    }
  }

  // Check if user profile exists
  private async checkProfileExists(uid: string): Promise<boolean> {
    const docRef = doc(this.firestore, 'users', uid);
    const docSnap = await getDoc(docRef);
    return docSnap.exists();
  }

  // Check if user has a pending invitation
  private async checkPendingInvitation(email: string): Promise<boolean> {
    try {
      const invitesRef = collection(this.firestore, 'invites');
      const q = query(
        invitesRef,
        where('email', '==', email),
        where('used', '==', false),
        limit(1)
      );
      const invitesSnapshot = await getDocs(q);
      return !invitesSnapshot.empty;
    } catch (error) {
      console.error('❌ Error checking pending invitation:', error);
      return false; // If error, assume no invitation to allow fallback profile creation
    }
  }

  // Create user profile
  private async createUserProfile(user: any): Promise<void> {
    console.log('🆕 Creating new user profile...');

    // Get first active center from Firestore
    let defaultCenterId = 'PRCETAŞE_UAG'; // Fallback to your center

    try {
      const centersRef = collection(this.firestore, 'centers');
      const q = query(centersRef, where('isActive', '==', true), limit(1));
      const centersSnapshot = await getDocs(q);

      if (!centersSnapshot.empty) {
        defaultCenterId = centersSnapshot.docs[0].id;
        console.log('✅ Assigned new user to center:', defaultCenterId);
      } else {
        console.warn('⚠️ No active centers found, using fallback:', defaultCenterId);
      }
    } catch (error) {
      console.error('❌ Error fetching centers, using fallback:', error);
    }

    const profile: UserProfile = {
      id: user.uid,
      email: user.email || '',
      firstName: user.displayName?.split(' ')[0] || 'User',
      lastName: user.displayName?.split(' ')[1] || '',
      role: 'PHYSICIAN',
      centerId: defaultCenterId, // Dynamically assigned from first active center
      createdAt: new Date(),
      updatedAt: new Date(),
      isActive: true,
      termsAccepted: false
    };

    console.log('💾 Saving new user profile to Firestore:', {
      email: profile.email,
      centerId: profile.centerId,
      role: profile.role
    });

    await setDoc(doc(this.firestore, 'users', user.uid), profile);
    this.userProfile.set(profile);

    console.log('✅ New user profile created successfully');
  }

  // Check if user is admin
  isAdmin(): boolean {
    return this.userProfile()?.role === 'ADMIN';
  }

  // Check if user is physician
  isPhysician(): boolean {
    return this.userProfile()?.role === 'PHYSICIAN';
  }

  // Send password reset email
  async sendPasswordResetEmail(email: string): Promise<void> {
    try {
      await sendPasswordResetEmail(this.auth, email);
    } catch (error) {
      console.error('Password reset error:', error);
      throw error;
    }
  }

  // Validate invite token
  async validateInviteToken(token: string): Promise<any> {
    try {
      // This would typically check a Firestore collection for invites
      const invitesRef = collection(this.firestore, 'invites');
      // Implementation would query for the token
      return { valid: true, email: '', role: 'PHYSICIAN' };
    } catch (error) {
      console.error('Token validation error:', error);
      throw error;
    }
  }

  // Link invited user
  async linkInvitedUser(token: string, userId: string): Promise<void> {
    try {
      // Implementation to link an invited user to their account
      console.log('Linking invited user:', token, userId);
    } catch (error) {
      console.error('Link invited user error:', error);
      throw error;
    }
  }

  // Complete invite registration
  async completeInviteRegistration(data: any): Promise<void> {
    try {
      // Implementation to complete the invite registration process
      console.log('Completing invite registration:', data);
    } catch (error) {
      console.error('Complete invite registration error:', error);
      throw error;
    }
  }

  // Refresh token to get latest custom claims
  async refreshToken(): Promise<void> {
    try {
      if (this.currentUser()) {
        await this.currentUser().reload();
        const idTokenResult = await this.currentUser().getIdTokenResult(true);
        console.log('Token refreshed, claims:', idTokenResult.claims);
      }
    } catch (error) {
      console.error('Token refresh error:', error);
      throw error;
    }
  }

  // Check if user has specific role
  hasRole(role: 'ADMIN' | 'PHYSICIAN' | 'VIEWER'): boolean {
    const userProfile = this.userProfile();
    if (!userProfile) return false;

    // For now, using profile role. In production, you might also check custom claims
    return userProfile.role === role;
  }

  // Check if user is viewer
  isViewer(): boolean {
    return this.userProfile()?.role === 'VIEWER';
// Check if user is super admin (has access to all centers)  isSuperAdmin(): boolean {    const profile = this.userProfile();    return profile?.isSuperAdmin === true && profile?.allCentersAccess === true;  }
  }
}
